import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { valorPorExtenso } from "@bookdental/schemas";
import { formatCep } from "@/lib/viacep";
import { codigoDoPedido, codigoDoServico } from "@/lib/codigo-do-pedido";
import { formatBRL } from "@/components/prosthesis/prosthesis-meta";
import { esc } from "./impressao";

type Endereco = {
  document?: string | null;
  phone?: string | null;
  zipCode?: string | null;
  address?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
};

export type DadosDoRecibo = {
  id: string;
  numero: number | null;
  paciente: string;
  dentista: string | null;
  laboratorio: (Endereco & { name: string; logoUrl?: string | null }) | null;
  cliente: Endereco & { name: string; tradeName?: string | null };
  servicos: {
    id: string;
    ordem: number;
    servico: string;
    material: string | null;
    dentes: string | null;
    cor: string | null;
    quantidade: number;
    valorUnitarioCents: number | null;
    /** Já é preço × quantidade. */
    valorCents: number | null;
  }[];
  retrabalhos: { serviceId: string; valorCents: number; notes: string }[];
  acrescimos: { descricao: string; valorCents: number | null }[];
};

const so = (d?: string | null) => (d ?? "").replace(/\D/g, "");
function documento(d?: string | null) {
  const n = so(d);
  if (n.length === 14) return `CNPJ ${n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")}`;
  if (n.length === 11) return `CPF ${n.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")}`;
  return null;
}
function endereco(e: Endereco) {
  if (!e.address) return null;
  return [
    `${e.address}${e.addressNumber ? `, ${e.addressNumber}` : ""}${e.addressComplement ? ` — ${e.addressComplement}` : ""}`,
    [e.neighborhood, e.city && e.state ? `${e.city}/${e.state}` : e.city].filter(Boolean).join(" · "),
    e.zipCode ? `CEP ${formatCep(e.zipCode)}` : null,
  ]
    .filter(Boolean)
    .join(" — ");
}

const CSS = `
@page { size: A4; margin: 18mm 16mm; }
body { font-size: 10.5pt; line-height: 1.45; }
header { display: flex; gap: 12px; align-items: center; border-bottom: 1.5px solid #000; padding-bottom: 10px; }
header img { max-height: 52px; max-width: 120px; object-fit: contain; }
header .nome { font-size: 14pt; font-weight: 700; }
header .info { font-size: 9pt; color: #333; }
h1 { font-size: 16pt; margin: 18px 0 4px; letter-spacing: 1px; }
.sub { font-size: 9.5pt; color: #333; margin-bottom: 14px; }
.texto { margin: 12px 0 14px; text-align: justify; }
table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
th, td { border-bottom: 1px solid #ccc; padding: 5px 6px; text-align: left; vertical-align: top; }
th { font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1.5px solid #000; }
td.valor, th.valor { text-align: right; white-space: nowrap; }
tfoot td { border-bottom: none; font-weight: 700; font-size: 11pt; padding-top: 8px; }
.assinatura { margin-top: 56px; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
.linha { border-top: 1px solid #000; padding-top: 4px; min-width: 260px; text-align: center; font-size: 9.5pt; }
.nota { font-size: 8.5pt; color: #444; margin-top: 6px; }
@media screen { body { max-width: 820px; margin: 0 auto; padding: 28px 32px; } }
`;

/** Recibo A4 do pedido: o laboratório declara que recebeu da clínica o valor dos serviços. */
export function htmlDoRecibo(d: DadosDoRecibo, hoje = new Date()) {
  const lab = d.laboratorio;
  const servicos = d.servicos;
  const semValor = servicos.filter((s) => s.valorCents == null).length;
  const retrabalhos = d.retrabalhos.map((r) => ({
    ...r,
    codigo: codigoDoServico(d, { ordem: d.servicos.find((s) => s.id === r.serviceId)?.ordem }),
  }));
  const total =
    servicos.reduce((t, s) => t + (s.valorCents ?? 0), 0) +
    retrabalhos.reduce((t, r) => t + r.valorCents, 0) +
    d.acrescimos.reduce((t, a) => t + (a.valorCents ?? 0), 0);
  const unitario = (s: DadosDoRecibo["servicos"][number]) =>
    s.quantidade > 1 && s.valorUnitarioCents != null ? `<div class="nota">${s.quantidade} × ${esc(formatBRL(s.valorUnitarioCents))}</div>` : "";
  const cliente = d.cliente.tradeName ? `${d.cliente.name} (${d.cliente.tradeName})` : d.cliente.name;
  const docCliente = documento(d.cliente.document);

  const linhas = [
    ...servicos.map(
      (s) => `<tr><td>${esc(codigoDoServico(d, s))}</td><td>${esc(s.servico)}${s.material ? ` — ${esc(s.material)}` : ""}</td>
<td>${esc(s.dentes || "—")}</td><td>${esc(s.cor || "—")}</td><td class="valor">${s.quantidade}</td><td class="valor">${s.valorCents == null ? "—" : esc(formatBRL(s.valorCents))}${unitario(s)}</td></tr>`,
    ),
    ...retrabalhos.map(
      (r) => `<tr><td>${esc(r.codigo)}</td><td colspan="4">Retrabalho cobrado${r.notes ? ` — ${esc(r.notes)}` : ""}</td>
<td class="valor">${esc(formatBRL(r.valorCents))}</td></tr>`,
    ),
    ...d.acrescimos.map(
      (a) => `<tr><td>${esc(codigoDoPedido(d))}</td><td colspan="4">${esc(a.descricao)}</td>
<td class="valor">${a.valorCents == null ? "—" : esc(formatBRL(a.valorCents))}</td></tr>`,
    ),
  ].join("");

  return `<header>
  ${lab?.logoUrl ? `<img src="${esc(lab.logoUrl)}" alt="">` : ""}
  <div>
    <div class="nome">${esc(lab?.name)}</div>
    <div class="info">${esc([documento(lab?.document), lab?.phone ? `Tel. ${lab.phone}` : null].filter(Boolean).join(" · "))}</div>
    <div class="info">${esc(lab ? endereco(lab) : "")}</div>
  </div>
</header>
<h1>RECIBO</h1>
<div class="sub">Pedido nº ${esc(codigoDoPedido(d))} · Paciente ${esc(d.paciente)}${d.dentista ? ` · Dentista ${esc(d.dentista)}` : ""}</div>
<p class="texto">Recebemos de <b>${esc(cliente)}</b>${docCliente ? `, ${esc(docCliente)}` : ""}${endereco(d.cliente) ? `, ${esc(endereco(d.cliente))}` : ""}, a importância de <b>${esc(formatBRL(total))}</b> (${esc(valorPorExtenso(total))}), referente aos serviços de prótese relacionados abaixo.</p>
<table>
  <thead><tr><th>Código</th><th>Serviço</th><th>Dentes</th><th>Cor</th><th class="valor">Qtd.</th><th class="valor">Valor</th></tr></thead>
  <tbody>${linhas}</tbody>
  <tfoot><tr><td colspan="5">Total</td><td class="valor">${esc(formatBRL(total))}</td></tr></tfoot>
</table>
${semValor ? `<p class="nota">${semValor === 1 ? "1 serviço sem valor definido não entra" : `${semValor} serviços sem valor definido não entram`} no total.</p>` : ""}
<div class="assinatura">
  <div>${esc(lab?.city ? `${lab.city}, ` : "")}${esc(format(hoje, "d 'de' MMMM 'de' yyyy", { locale: ptBR }))}</div>
  <div class="linha">${esc(lab?.name)}</div>
</div>`;
}

export const CSS_DO_RECIBO = CSS;
